import React, { useState, useEffect, useRef } from "react";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import VybogTALLoaderAnimation from "../Dynamic-Animations/DynamicAnimations";
import Button from "../Button/Button";
import CustomTooltip from "../Tooltip/Tooltip";
import HtmlResumePreview from "./HtmlResumePreview";

const RefreshIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M23 4v6h-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1 20v-6h6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const ResumeViewer = ({ url, name }) => {
  const getPdfJsVersion = () => {
    try {
      const pdfjsDist = require("pdfjs-dist/package.json");
      return pdfjsDist.version;
    } catch (error) {
      return "3.11.174";
    }
  };
  const workerUrl = `//unpkg.com/pdfjs-dist@${getPdfJsVersion()}/build/pdf.worker.min.js`;
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef(null);
  const isPdf = name?.toLowerCase().endsWith(".pdf");
  const isHtml = /\.html?$/i.test(name || "");
  const isDoc =
    name?.toLowerCase().endsWith(".doc") ||
    name?.toLowerCase().endsWith(".docx");

  const handlePdfError = (error) => {
    setError(
      "Failed to load the PDF file. This could be due to an invalid file, network issues, or an unsupported format. Please check the file URL or try a different file.",
    );
    setIsLoading(false);
  };

  const handleDocError = () => {
    setError(
      "Failed to load the document. This could be due to an invalid file, network issues, or an unsupported format. Please ensure the file is accessible or try a different file.",
    );
    setIsLoading(false);
  };

  const handleRefresh = () => {
    if (isDoc && iframeRef.current) {
      iframeRef.current.src = `${url}`;
    }
  };

  useEffect(() => {
    if (isDoc && iframeRef.current) {
      iframeRef.current.src = `${url}`;
    }
    setIsLoading(true);
    setError(null);
  }, [url, isDoc]);

  return (
    <div style={viewerContainerStyle}>
      {error ? (
        <div style={errorStyle}>
          <h3>Error Loading File</h3>
          <p>{error}</p>
        </div>
      ) : isPdf ? (
        <Worker workerUrl={workerUrl}>
          <Viewer
            fileUrl={url}
            onDocumentLoadError={handlePdfError}
            onLoadSuccess={() => setIsLoading(false)}
          />
        </Worker>
      ) : isHtml ? (
        <HtmlResumePreview key={url} url={url} />
      ) : isDoc ? (
        <div style={{ width: "100%", height: "100%", position: "relative" }}>
          <div
            style={{
              position: "absolute",
              top: "4px",
              right: "30px",
              zIndex: 1100,
            }}
          >
            <CustomTooltip
              title="Refresh if the document takes too long to load."
              placement="bottom"
            >
              <Button
                event={handleRefresh}
                style={{ ...refreshButtonStyle, position: "static" }}
                icon={<RefreshIcon />}
              />
            </CustomTooltip>
          </div>
          {isLoading && (
            <div style={loadingStyle}>
              <VybogTALLoaderAnimation />
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`}
            style={iframeStyle}
            title="Document Viewer"
            allowFullScreen
            onError={handleDocError}
            onLoad={() => setIsLoading(false)}
          />
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "20px" }}>
          Unsupported file format: {name}
        </div>
      )}
    </div>
  );
};

const viewerContainerStyle = {
  width: "100%",
  height: "100%",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  overflow: "hidden",
  backgroundColor: "#fff",
};

const iframeStyle = {
  width: "100%",
  height: "100%",
  border: "none",
  background: "white",
};

const errorStyle = {
  textAlign: "center",
  padding: "20px",
  color: "#d32f2f",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
};

const refreshButtonStyle = {
  position: "absolute",
  top: "10px",
  right: "50px",
  zIndex: 1000,
  padding: "6px 12px",
  backgroundColor: "#fff",
  color: "black",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "500",
  // border: "none",
  marginRight: "30px",
};

const loadingStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  color: "#1976d2",
  fontSize: "18px",
};

export default ResumeViewer;
